import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useTheme } from "../../hooks";
// import { useAuth } from "../../hooks/chat/useAuth";
import { getAuthData } from "../../contexts/AuthContext";
import { getAiApiUrl } from "../../config";

interface LinkPreviewProps {
  href: string;
  children: React.ReactNode;
}

interface LinkMetadata {
  title: string;
  description: string;
  image: string;
  logo: string;
  url: string;
  loading: boolean;
  error: boolean;
}

const defaultMetadata: LinkMetadata = {
  title: "",
  logo: "",
  description: "",
  image: "",
  url: "",
  loading: true,
  error: false,
};

export const LinkPreview: React.FC<LinkPreviewProps> = ({ href, children }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [metadata, setMetadata] = useState<LinkMetadata>(defaultMetadata);
  const previewRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { theme } = useTheme();

  const isDark =
    theme === "dark" ||
    (theme === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  // Store the current URL to detect changes
  const urlRef = useRef<string>(href);

  // Reset metadata when URL changes
  useEffect(() => {
    if (urlRef.current !== href) {
      console.log("URL changed, resetting metadata");
      urlRef.current = href;
      setMetadata({
        ...defaultMetadata,
        url: href,
      });
    }
  }, [href]);

  // Fetch metadata when preview is shown
  useEffect(() => {
    if (showPreview && metadata.loading && !metadata.error) {
      console.log("Fetching metadata for:", href);
      const fetchMetadata = async () => {
        try {

          // Use a proxy service to avoid CORS issues
          const auth = await getAuthData();
          const response = await fetch(
            `${getAiApiUrl()}/link-preview?url=${encodeURIComponent(href)}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${auth.jwt}`,
              },
              body: JSON.stringify({
                walletAddress: auth.walletAddress,
                nonce: auth.nonce,
              }),
            }
          );
          if (!response.ok) {
            throw new Error("Failed to fetch link preview");
          }

          const data = await response.json();
          console.log("Received metadata:", data);

          setMetadata({
            title: data.title || "",
            description: data.description || "",
            image: data.image || "",
            logo: data.logo || "",
            url: href,
            loading: false,
            error: false,
          });
        } catch (error) {
          console.error("Error fetching link preview:", error);
          setMetadata({
            ...defaultMetadata,
            url: href,
            loading: false,
            error: true,
          });
        }
      };

      fetchMetadata();
    }
  }, [showPreview, href, metadata.loading, metadata.error]);

  // Handle mouse events with delay to prevent flickering
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      console.log("Mouse enter, showing preview for:", href);
      setShowPreview(true);

      // Reset metadata if it's for a different URL or has error
      if (metadata.url !== href || metadata.error) {
        setMetadata({
          ...defaultMetadata,
          url: href,
        });
      }
    }, 300); // 300ms delay before showing preview
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      console.log("Mouse leave, hiding preview");
      setShowPreview(false);
    }, 300); // 300ms delay before hiding preview
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Position the preview based on the link's position
  useEffect(() => {
    if (showPreview && previewRef.current) {
      const linkRect = document.querySelector(`a[href="${href}"]`)?.getBoundingClientRect();
      
      if (linkRect) {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const previewWidth = 320; // Width of the preview
        const previewHeight = metadata.image ? 500 : 200; // Approximate height
        
        // Calculate position
        let top = linkRect.top - previewHeight - 10; // 10px gap
        let left = linkRect.left + (linkRect.width / 2) - (previewWidth / 2);
        
        // Adjust if off screen
        if (top < 10) {
          // If not enough space above, position below
          top = linkRect.bottom + 10;
        }
        
        // Ensure it doesn't go off the sides
        if (left < 10) {
          left = 10;
        } else if (left + previewWidth > viewportWidth - 10) {
          left = viewportWidth - previewWidth - 10;
        }
        
        // Apply position
        previewRef.current.style.transform = 'none';
        previewRef.current.style.top = `${top}px`;
        previewRef.current.style.left = `${left}px`;
      }
    }
  }, [showPreview, href, metadata.image]);

  return (
    <span
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 dark:text-blue-400 hover:underline break-all"
        style={{ overflowWrap: "break-word", wordBreak: "break-all" }}
      >
        {children}
      </a>

      {showPreview && ReactDOM.createPortal(
        <div
          ref={previewRef}
          className={`fixed z-[9999] shadow-xl overflow-hidden ${
            isDark
              ? "bg-gray-800 border-2 border-blue-400"
              : "bg-white border-2 border-blue-400"
          }`}
          style={{ 
            opacity: metadata.loading ? 0.7 : 1,
            borderRadius: "25px",
            width: "320px",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
          }}
        >
          {metadata.loading ? (
            <div className="p-4 flex items-center justify-center">
              <div className="animate-pulse flex space-x-2">
                <div className="h-2 w-2 bg-blue-400 rounded-full"></div>
                <div className="h-2 w-2 bg-blue-400 rounded-full"></div>
                <div className="h-2 w-2 bg-blue-400 rounded-full"></div>
              </div>
            </div>
          ) : metadata.error ? (
            <div className="p-4">
              <p
                className={`text-sm ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Preview not available
              </p>
            </div>
          ) : (
            <>
              {metadata.image && (
                <div className="w-full h-80 bg-gray-100 dark:bg-gray-700 overflow-hidden" style={{ borderTopLeftRadius: "25px", borderTopRightRadius: "25px" }}>
                  <img
                    src={metadata.image}
                    alt={metadata.title || "Link preview"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Hide image on error
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-center mb-2">
                  <div className="w-16 h-16 mr-2 mb-4 flex-shrink-0">
                    <img
                      src={`${metadata?.logo ? metadata?.logo : href.indexOf('goplausible.xyz')>-1 ? 'https://agency.goplausible.xyz/images/Agent.png':metadata.image}`}
                      alt=""
                      className="w-full h-full"
                    />
                  </div>
                  <p className="text-xl text-gray-500 dark:text-gray-400 truncate">
                    {new URL(href).hostname}
                  </p>
                </div>
                {metadata.title && (
                  <h3
                    className={`font-medium text-sm mt-4 mb-1 line-clamp-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {metadata.title}
                  </h3>
                )}
                {metadata.description && (
                  <p
                    className={`text-sm line-clamp-3 ${
                      isDark ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    {metadata.description}
                  </p>
                )}
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </span>
  );
};
